'use client'

import { useEffect, type ReactNode } from 'react'
import { usePathname } from 'next/navigation'
import { useThemeStore, type Theme } from '@/stores/theme-store'
import { isDashboardRoute } from '@/lib/dashboard-routes'

/**
 * Hydrates the client-side theme store from the server-read cookie, and keeps
 * `<html>`'s `dark` class in sync with it. `<html>` is the one ancestor every
 * Radix portal (Dialog/DropdownMenu/Popover/Select/Tooltip all mount under
 * `document.body`, a sibling of any shell-level div) is guaranteed to have —
 * applying `dark` lower down (as the shells used to) never reached them.
 *
 * The stored preference is dashboard-only by intent: `dark` is only ever added to `<html>` while
 * the current route is inside a dashboard (`isDashboardRoute`), so every other page (auth,
 * onboarding/portfolio-audit flows) always renders light even if the user last left a dashboard
 * in dark mode — e.g. right after logging out. See the matching blocking script in
 * `app/layout.tsx` for the equivalent hard-load/no-flash version of this same check.
 */
export function ThemeProvider({
  initialTheme,
  children,
}: {
  initialTheme: Theme
  children: ReactNode
}) {
  const theme = useThemeStore((s) => s.theme)
  const pathname = usePathname()

  useEffect(() => {
    useThemeStore.setState({ theme: initialTheme })
  }, [initialTheme])

  useEffect(() => {
    document.documentElement.classList.toggle(
      'dark',
      theme === 'dark' && isDashboardRoute(pathname),
    )
  }, [theme, pathname])

  return <>{children}</>
}
